import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminReinvites() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState(null); // email being approved
  const [approved, setApproved] = useState(new Set());
  const [approveError, setApproveError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await bookmarkService.listReinviteRequests();
      setRequests(data.requests || []);
    } catch (err) {
      setError('Failed to load reinvite requests.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (email) => {
    setApproving(email);
    setApproveError('');
    try {
      await bookmarkService.approveReinviteRequest(email);
      setApproved(prev => new Set([...prev, email]));
      setRequests(prev => prev.filter(r => r.email !== email));
    } catch (err) {
      console.error(err);
      setApproveError(`Failed to approve reinvite for ${email}.`);
    } finally {
      setApproving(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
          <ArrowLeftIcon className="w-5 h-5" />
        </Link>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Reinvite Requests</h1>
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40"
          title="Refresh"
        >
          <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Users who clicked an expired or already-used invitation link and requested a new one.
        Approving sends a fresh 48-hour magic link to their email.
      </p>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>
      )}
      {approveError && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm rounded-lg px-4 py-3 mb-4">{approveError}</div>
      )}

      {approved.size > 0 && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 text-sm rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <CheckCircleIcon className="w-4 h-4 shrink-0" />
          {approved.size === 1
            ? `Reinvite sent to ${[...approved][0]}.`
            : `Reinvites sent to ${approved.size} users.`}
        </div>
      )}

      {loading && requests.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">No pending reinvite requests.</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
          {requests.map(req => (
            <div key={req.email} className="flex items-center justify-between px-5 py-4 gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{req.email}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Requested {fmtDate(req.requestedAt)}</p>
              </div>
              <button
                onClick={() => handleApprove(req.email)}
                disabled={approving === req.email}
                className="shrink-0 bg-primary-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {approving === req.email ? 'Sending…' : 'Approve & resend'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
